import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import { Copy, ChevronDown, ChevronUp, Upload, X } from 'lucide-react';
import { confirmPayment, getPackageDetail, getPaymemtInfo } from '@/api/package';
import { PackageAPI, PaymentInfo } from '@/api/type';
import { message } from 'antd';

const Checkout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const query = new URLSearchParams(location.search);
  const contractId = query.get('contractId') ? Number(query.get('contractId')) : (id ? Number(id) : null);
  const { responseContract } = location.state || {};

  const [uploadProf, setUploadProf] = useState<boolean>(false);
  const [packageDetail, setPackageDetail] = useState<PackageAPI | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const [showOrderDetails, setShowOrderDetails] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);

  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>(
    {
      status: false,
      data: {
        amount: '',
        transaction_code: '',
        bank_info: {
          bank_name: '',
          account_no: '',
          account_name: '',
          content: '',
        },
        contract_info: {
          full_name: '',
          id_number: '',
        }
      },
    }
  );
  const [imageQR, setImageQR] = useState<string>('');

  const copyToClipboard = async (text: string, field: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedField(field);
        message.success('Đã sao chép');
        setTimeout(() => setCopiedField(null), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        message.error('Không thể sao chép. Vui lòng thử lại.');
      }
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedField(field);
        message.success('Đã sao chép');
        setTimeout(() => setCopiedField(null), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
        message.error('Không thể sao chép. Vui lòng thử lại.');
      }
      document.body.removeChild(textArea);
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', path: '/home' },
    { label: 'Gói đầu tư', path: '/plans' },
    { label: 'Thanh toán' },
  ];

  function confirmPaymentUpload() {
    setUploadProf(true);
    if (!contractId) {
      message.error('No contract ID found for payment confirmation');
      return;
    }
    if (!receiptFile) {
      message.error('Please upload a receipt file before confirming payment');
      return;
    }
    confirmPayment(contractId, receiptFile, paymentInfo.data ? paymentInfo.data.transaction_code : '')
      .then((data) => {
        message.success(`${data.message}`);
        navigate('/profile/investment');
      })
      .catch((error) => {
        console.error('Failed to confirm payment:', error);
        message.error('Failed to confirm payment. Please try again later.');
      });
    setUploadProf(false);
  }


  useEffect(() => {
    if (id) {
      getPackageDetail(Number(id))
        .then((data) => {
          setPackageDetail(data);
        })
        .catch((error) => {
          console.error('Failed to fetch package detail:', error);
          message.error('Không thể tải thông tin gói đầu tư. Vui lòng thử lại sau.');
        });
    }
  }, [id]);

  useEffect(() => {
    const targetId = contractId || responseContract?.id;

    if (targetId) {
      getPaymemtInfo(targetId)
        .then((data) => {
          let info = data;
          if (!info.data && (info as any).bank_info) {
            info = {
              status: true,
              data: info as any
            };
          }

          setPaymentInfo(info);
          const qrData = `https://img.vietqr.io/image/${info.data?.bank_info.bank_name}-${info.data?.bank_info.account_no}-compact.png?amount=${info.data?.amount}&addInfo=${encodeURIComponent(info.data?.bank_info.content || '')}&accountName=${encodeURIComponent(info.data?.bank_info.account_name || '')}`;
          setImageQR(qrData);
        })
        .catch((error) => {
          console.error('Failed to fetch payment info:', error);
          message.error('Không thể tải thông tin thanh toán. Vui lòng thử lại sau.');
        });
    }
  }, [responseContract, contractId]);

  const formatAmount = (amount: string | number) => {
    return Number(amount).toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="!bg-gray-100 dark:!bg-dark-bg !py-4 !px-4">
      <Helmet>
        <title>Thanh toán - BeQs Reward</title>
      </Helmet>

      <PageHeader title="Thanh toán hợp đồng" breadcrumbs={breadcrumbs} />

      <div className="!mx-auto">
        {/* Order Details Section */}
        <div className="!bg-white dark:!bg-dark-card !shadow-sm rounded-sm !mb-4">
          <div
            className="!flex !justify-between !items-center !p-4 !cursor-pointer"
            onClick={() => setShowOrderDetails(!showOrderDetails)}
          >
            <span className="!text-gray-700 dark:!text-gray-300 !font-medium">Chi tiết đơn hàng</span>
            <div className="!flex !items-center !gap-2">
              <span className="!text-green-600 dark:!text-green-400 !text-sm">
                {showOrderDetails ? 'Ẩn' : 'Hiện'}
              </span>
              {showOrderDetails ? (
                <ChevronUp className="!w-4 !h-4 !text-green-600 dark:!text-green-400" />
              ) : (
                <ChevronDown className="!w-4 !h-4 !text-green-600 dark:!text-green-400" />
              )}
            </div>
          </div>

          {showOrderDetails && (
            <div className="!px-4 !pb-4">
              <div className="!flex !justify-between !items-start !py-2 !border-t !border-gray-100 dark:!border-gray-700">
                <div>
                  <p className="!text-gray-900 dark:!text-white !font-medium !mb-0">
                    {packageDetail?.name || 'Đang tải...'}
                  </p>
                  <p className="!text-gray-500 dark:!text-gray-400 !text-sm !mb-0">
                    x {packageDetail?.duration_months || 1} tháng
                  </p>
                </div>
                <span className="!text-gray-900 dark:!text-white">
                  {formatAmount(packageDetail?.price || 0)} vnd
                </span>
              </div>

              <div className="!flex !justify-between !items-center !pt-3 !border-t !border-gray-200 dark:!border-gray-600">
                <span className="!text-gray-700 dark:!text-gray-300 !font-semibold">Tổng cộng:</span>
                <span className="!text-gray-900 dark:!text-white !font-bold">
                  {formatAmount(paymentInfo.data?.amount || packageDetail?.price || 0)} vnd
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Payment QR Section */}
        <div className="!bg-white dark:!bg-dark-card !shadow-sm rounded-sm !p-4">
          {/* Instruction */}
          <div className="!flex !items-center !gap-2 mb-8 !text-center !justify-center">
            <svg className="!w-5 !h-5 !text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <p className="!text-gray-600 dark:!text-gray-400 !text-sm">
              Mở App Ngân hàng bất kỳ để <strong>quét mã VietQR</strong> hoặc <strong>chuyển khoản</strong> chính xác số tiền bên dưới
            </p>
          </div>

          {/* QR and Bank Info */}
          <div className="!flex !flex-col md:!flex-row justify-center !items-center md:gap-32">
            {/* QR Code */}
            <div className="!flex !flex-col !items-center w-full md:!w-auto !mb-6 md:!mb-0">
              <div className="bg-white p-2 shadow-sm">
                <img
                  src={imageQR}
                  alt="VietQR Payment"
                  className="!w-64 !h-64 !object-contain"
                />
              </div>
              <p className="!text-gray-600 dark:!text-gray-400 !text-sm !mt-3 text-center">
                Lưu ý : Nhập chính xác số tiền <strong className="!text-gray-900 dark:!text-white">{formatAmount(paymentInfo.data?.amount || 0)} vnd</strong> khi chuyển khoản
              </p>
            </div>

            {/* Bank Info */}
            <div className="!flex flex-col w-[400px] space-y-3">
              {/* Bank Name */}
              <div className="!flex !items-center !gap-2">
                <div className="!w-8 !h-8 flex !items-center !justify-center">
                  <img src="/images/logo/vcb.png" alt="" />
                </div>
                <div>
                  <p className="!text-gray-500 dark:!text-gray-400 !text-xs !mb-0">Ngân hàng</p>
                  <p className="!text-gray-900 dark:!text-white !font-semibold !mb-0">
                    {paymentInfo.data?.bank_info.bank_name || 'Đang tải...'}
                  </p>
                </div>
              </div>

              {/* Account Holder */}
              <div className="!flex flex-col !justify-between !items-start">
                <p className="!text-gray-500 dark:!text-gray-400 !text-xs !mb-0">Chủ tài khoản :</p>
                <p className="!text-gray-900 dark:!text-white !font-semibold !mb-0">
                  {paymentInfo.data?.bank_info.account_name || 'Đang tải...'}
                </p>
              </div>

              {/* Account Number */}
              <div className="!flex !justify-between !items-center">
                <div>
                  <p className="!text-gray-500 dark:!text-gray-400 !text-xs !mb-0">Số tài khoản :</p>
                  <p className="!text-blue-600 dark:!text-blue-400 !font-semibold !mb-0">
                    {paymentInfo.data?.bank_info.account_no || 'Đang tải...'}
                  </p>
                </div>
                <button
                  onClick={() => copyToClipboard(paymentInfo.data?.bank_info.account_no || '', 'account')}
                  className="!px-3 !py-1 !border !border-gray-300 dark:!border-gray-600 !text-sm !text-gray-600 dark:!text-gray-400 hover:!bg-gray-50 dark:hover:!bg-gray-700 !transition-colors !flex !items-center !gap-1"
                >
                  <Copy className="!w-3 !h-3" />
                  {copiedField === 'account' ? 'Đã chép' : 'Sao chép'}
                </button>
              </div>

              {/* Amount */}
              {/* <div className="!flex !justify-between !items-center">
                <div>
                  <p className="!text-gray-500 dark:!text-gray-400 !text-xs !mb-0">Số tiền :</p>
                  <p className="!text-green-600 dark:!text-green-400 !font-bold !text-lg !mb-0">
                    {formatAmount(paymentInfo.data?.amount || 0)} vnd
                  </p>
                </div>
                <button
                  onClick={() => copyToClipboard(paymentInfo.data?.amount || '', 'amount')}
                  className="!px-3 !py-1 !border !border-gray-300 dark:!border-gray-600 !text-sm !text-gray-600 dark:!text-gray-400 hover:!bg-gray-50 dark:hover:!bg-gray-700 !transition-colors !flex !items-center !gap-1"
                >
                  <Copy className="!w-3 !h-3" />
                  {copiedField === 'amount' ? 'Đã chép' : 'Sao chép'}
                </button>
              </div> */}

              {/* Content */}
              <div className="!flex !justify-between !items-center">
                <div>
                  <p className="!text-gray-500 dark:!text-gray-400 !text-xs !mb-0">Nội dung :</p>
                  <p className="!text-blue-600 dark:!text-blue-400 !font-semibold !mb-0">
                    {paymentInfo.data?.bank_info.content || 'Đang tải...'}
                  </p>
                </div>
                <button
                  onClick={() => copyToClipboard(paymentInfo.data?.bank_info.content || '', 'content')}
                  className="!px-3 !py-1 !border !border-gray-300 dark:!border-gray-600 !text-sm !text-gray-600 dark:!text-gray-400 hover:!bg-gray-50 dark:hover:!bg-gray-700 !transition-colors !flex !items-center !gap-1"
                >
                  <Copy className="!w-3 !h-3" />
                  {copiedField === 'content' ? 'Đã chép' : 'Sao chép'}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="!mt-6 !pt-4 !border-t !border-gray-200 dark:!border-gray-700">
            

            {/* Compact Upload Section */}
            <div className="!flex !flex-col md:!flex-row !items-center justify-between !gap-3">
              {receiptFile ? (
                <div className="!flex !items-center !gap-3 !flex-1">
                  <img
                    src={URL.createObjectURL(receiptFile)}
                    alt="Receipt Preview"
                    className="!w-16 !h-16 !object-cover !border !border-gray-200 !cursor-pointer hover:!opacity-80 !transition-opacity"
                    onClick={() => setShowImagePreview(true)}
                    title="Nhấn để xem ảnh lớn"
                  />
                  <div className="!flex-1">
                    <p className="!text-sm !text-gray-700 dark:!text-gray-300 !mb-0 !truncate">{receiptFile.name}</p>
                    <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-0">{(receiptFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    onClick={() => setReceiptFile(null)}
                    className="!p-1 !text-gray-400 hover:!text-red-500 !transition-colors"
                  >
                    <X className="!w-4 !h-4" />
                  </button>
                </div>
              ) : (
                <label className="!w-80 !flex !items-center !gap-2 !px-4 !py-2 !border !border-dashed !border-gray-300 dark:!border-gray-600 !cursor-pointer hover:!bg-gray-50 dark:hover:!bg-gray-700 !transition-colors">
                  <Upload className="!w-4 !h-4 !text-gray-500" />
                  <span className="!text-sm !text-gray-600 dark:!text-gray-400">Tải lên bằng chứng thanh toán</span>
                  <input
                    type="file"
                    className="!hidden"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setReceiptFile(file);
                    }}
                  />
                </label>
              )}

              <button
                onClick={confirmPaymentUpload}
                disabled={!receiptFile || uploadProf}
                className="!px-6 !py-2 !bg-orange-600 hover:!bg-orange-700 disabled:!bg-gray-300 disabled:!cursor-not-allowed !text-white !font-medium !transition-colors !whitespace-nowrap"
              >
                {uploadProf ? 'Đang xử lý...' : 'Xác nhận thanh toán'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview */}
      {showImagePreview && receiptFile && (
        <div
          className="!fixed !inset-0 !bg-black/70 !flex !items-center !justify-center !z-50 !p-4"
          onClick={() => setShowImagePreview(false)}
        >
          <div className="!relative !max-w-3xl !max-h-[90vh]">
            <button
              onClick={() => setShowImagePreview(false)}
              className="!absolute !-top-10 !right-0 !text-white hover:!text-gray-300 !transition-colors"
            >
              <X className="!w-8 !h-8" />
            </button>
            <img
              src={URL.createObjectURL(receiptFile)}
              alt="Receipt Preview"
              className="!max-w-full !max-h-[85vh] !object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
